import { FC } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";

import {
  AllotmentPolicies,
  PrincipalInfoGT,
  StoragesTemplates,
} from "../../../../../redux/slices/grainTracker/grainTrackerTypes";
import { principalInformationStyles } from "./qualityCriteriaParts/qualityCriteriaPartsStyles/PrincipalInformationGrainTrackerStyles";
import { changePosInArrayMultiple } from "../../../../data/dataHelpers/DataProgress";

type PrincipalInformationGrainTrackerParams = {
  t: (arg0: string) => string;
  storage: string | undefined;
  handleStorageId: (event: SelectChangeEvent) => void;
  storagesTemplates: StoragesTemplates | undefined;
  name: string;
  setNamePIGT: (principalInfoGT: PrincipalInfoGT, name: string) => void;
  principalInfoGT: PrincipalInfoGT;
  specie: any;
  handleChangeSpecies: (event: SelectChangeEvent) => void;
  addOptionByDefault: {
    id: number;
    slug: string;
    textIdLocale: string;
  }[];
  tonsCapacity: number | undefined;
  setTonsPIGT: (principalInfoGT: PrincipalInfoGT, tons: number) => void;
  editedLoading: boolean;
  AllotmentPolicies: AllotmentPolicies[];
  selectedCriteriaToDelete: string[];
};

export const PrincipalInformationGrainTracker: FC<
  PrincipalInformationGrainTrackerParams
> = ({
  t,
  storage,
  handleStorageId,
  storagesTemplates,
  name,
  setNamePIGT,
  principalInfoGT,
  specie,
  handleChangeSpecies,
  addOptionByDefault,
  tonsCapacity,
  setTonsPIGT,
  editedLoading,
  AllotmentPolicies,
  selectedCriteriaToDelete,
}) => {
  // Extracted and simplified the disabled logic for the species select
  const isSpeciesSelectDisabled = AllotmentPolicies.length > 0;

  const barleyID = 1;
  const softWheatID = 4;
  const wheatID = 16;
  const soybeanID = 18;

  const safeOptions = addOptionByDefault ?? [];

  const wheatExists =
    safeOptions.findIndex((option) => option?.id === wheatID) !== -1;

  const newValuesPosition = changePosInArrayMultiple(safeOptions, [
    {
      from: safeOptions.findIndex((option) => option?.id === barleyID),
      to: 1,
    },
    {
      from: safeOptions.findIndex((option) => option?.id === softWheatID),
      to: 2,
    },
    {
      from: safeOptions.findIndex((option) => option?.id === wheatID),
      to: 3,
    },
    {
      from: safeOptions.findIndex((option) => option?.id === soybeanID),
      to: wheatExists ? 4 : 3,
    },
  ]);

  const manageName = (event: any) => {
    const newStorageId = event.target.value;
    const selectedName =
      storagesTemplates?.data.find((item) => String(item.id) === newStorageId)
        ?.nameTextId.text ?? "";
    setNamePIGT({ ...principalInfoGT, storage: newStorageId }, selectedName);
  };

  return (
    <FormControl sx={principalInformationStyles.FormControl} fullWidth>
      <InputLabel>{`${t("Grain_tracker.type-label")} *`}</InputLabel>
      <Select
        required
        value={storage}
        label={t("Grain_tracker.type-label")}
        onChange={manageName}
        disabled={editedLoading}
      >
        {storagesTemplates?.data.map((values) => {
          return (
            <MenuItem key={values.id} value={String(values.id)}>
              {values.nameTextId.text}
            </MenuItem>
          );
        })}
      </Select>

      <TextField
        sx={principalInformationStyles.FormControl}
        label={t("Grain_tracker.name-label")}
        placeholder={t("Grain_tracker.name-placeholder")}
        required
        value={name}
        disabled={editedLoading}
        onChange={(e: any) => setNamePIGT(principalInfoGT, e.target.value)}
      />
      <div className="specie-ton-container-graintracker">
        <Tooltip
          title={
            AllotmentPolicies.length > 0
              ? t("Grain_tracker.info_criteria_selected")
              : ""
          }
          arrow
          placement="top"
        >
          <FormControl
            sx={principalInformationStyles.FormControlSpecieContainer}
            disabled={editedLoading}
          >
            <InputLabel>{`${t(
              "Grain_tracker.expected-species-label",
            )} *`}</InputLabel>
            <Select
              required
              value={specie}
              label={t("Grain_tracker.expected-species-label")}
              onChange={handleChangeSpecies}
              disabled={isSpeciesSelectDisabled}
            >
              {newValuesPosition.map((values) => {
                if (values?.id === 0) {
                  return (
                    <MenuItem disabled key={values.id} value={values.id}>
                      {values.textIdLocale}
                    </MenuItem>
                  );
                } else {
                  return (
                    <MenuItem key={values?.id} value={values?.id}>
                      {values?.textIdLocale}
                    </MenuItem>
                  );
                }
              })}
            </Select>
          </FormControl>
        </Tooltip>
        <Tooltip
          title={
            tonsCapacity === 0
              ? t("Grain_tracker.capacity_zero_warning")
              : (tonsCapacity ?? 0) >= 999999
                ? t("Grain_tracker.capacity_max_warning")
                : ""
          }
          arrow
          placement="top"
        >
          <TextField
            sx={{
              ...principalInformationStyles.AvailableCapacityTextField,
              backgroundColor: tonsCapacity === 0 ? "#FFF8E1" : "white",
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            label={t("Grain_tracker.available-capacity-label")}
            type="number"
            placeholder={t("Grain_tracker.storage-unit")}
            InputProps={{ inputProps: { step: "any", min: 0, max: 1000000 } }}
            required
            variant="outlined"
            value={tonsCapacity ?? 0}
            disabled={editedLoading}
            onChange={(e) => {
              const value = parseFloat(e.currentTarget.value);
              const limitedValue = value > 1000000 ? 999999 : value;
              setTonsPIGT(principalInfoGT, limitedValue);
            }}
          />
        </Tooltip>
      </div>
      <div style={principalInformationStyles.requiredFieldsHintContainer}>
        <span style={principalInformationStyles.requiredFieldsHintText}>
          {t("Grain_tracker.required_fields_hint")}
        </span>
      </div>
    </FormControl>
  );
};
